import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { MainRoutes } from "common/config";
import {
  Build as WrenchIcon,
  Delete as TrashIcon,
  ArrowBack as ArrowLeftIcon,
  Add as PlusIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as TimesCircleIcon,
  Warning as ExclamationCircleIcon,
  Download as DownloadIcon,
  Science as FlaskIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import { CircularProgress } from "@mui/material";
import { Button } from "common/components";
import Modal from "common/components/base/modal/Modal";
import { ToastMessageUtil } from "common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "common/enums/controls/toast.enum";
import {
  Tool,
  TestCase,
  FieldInput,
  ValidationResult
} from "modules/configuration-hub/data/models/configuration-hub.model";
import { ConfigurationHubService } from "modules/configuration-hub/data/services/configuration-hub.service";
import "./AvailableTools.scss";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";

const AvailableTools = () => {
  const navigate = useNavigate();
  const [toolToDelete, setToolToDelete] = useState<string | null>(null);
  const [customTools, setCustomTools] = useState<Tool[]>([]);
  const [apiTools, setApiTools] = useState<Tool[]>([]);
  const [isLoadingTools, setIsLoadingTools] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Static tools - always available
  const staticTools: Tool[] = [
    {
      id: "1",
      name: "validate_days_difference_tool",
      displayName: "Days Difference Validator",
      description:
        "Validates the difference in days between two dates and ensures it falls within specified thresholds",
      status: "approved"
    },
    {
      id: "2",
      name: "unique_list_check_tool",
      displayName: "Unique List Checker",
      description:
        "Checks if all items in a list are unique and identifies any duplicate entries",
      status: "approved"
    },
    {
      id: "3",
      name: "validate_state_country_tool",
      displayName: "State Country Validator",
      description:
        "Validates that a given state belongs to the specified country and checks geographical consistency",
      status: "approved"
    },
    {
      id: "4",
      name: "reconcile_tool",
      displayName: "Reconcile Tool",
      description:
        "Reconciles data between two datasets, identifying matches, mismatches, and missing records",
      status: "approved"
    },
    {
      id: "5",
      name: "outlier_tool",
      displayName: "Outlier Detector",
      description:
        "Detects statistical outliers in numerical data using standard deviation and IQR methods",
      status: "approved"
    },
    {
      id: "6",
      name: "address_validation_tool",
      displayName: "Address Validator",
      description:
        "Validates address formats, completeness, and verifies against standard address conventions",
      status: "approved"
    },
    {
      id: "7",
      name: "categorical_outlier_tool",
      displayName: "Categorical Outlier Detector",
      description:
        "Identifies unusual or unexpected categorical values that deviate from known valid categories",
      status: "approved"
    }
  ];

  // Refresh function for manual data reload
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await ConfigurationHubService.getAllTools();
      if (response.statusCode === 200 && response.value) {
        setApiTools(response.value);
        ToastMessageUtil.show(
          "Tools refreshed successfully",
          ToastVariant.SUCCESS
        );
      } else {
        console.error("Failed to fetch tools:", response.errors);
        ToastMessageUtil.show("Failed to refresh tools", ToastVariant.ERROR);
      }
    } catch (error) {
      console.error("Error refreshing tools:", error);
      ToastMessageUtil.show("Error refreshing tools", ToastVariant.ERROR);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Load custom tools from localStorage and fetch API tools
  useEffect(() => {
    const loadData = async () => {
      // Fetch tools from API first
      try {
        setIsLoadingTools(true);
        const response = await ConfigurationHubService.getAllTools();
        if (response.statusCode === 200 && response.value) {
          setApiTools(response.value);
        } else {
          console.error("Failed to fetch tools:", response.errors);
          ToastMessageUtil.show(
            "Failed to load available tools",
            ToastVariant.ERROR
          );
        }
      } catch (error) {
        console.error("Error loading tools:", error);
        ToastMessageUtil.show(
          "Error loading available tools",
          ToastVariant.ERROR
        );
      } finally {
        setIsLoadingTools(false);
      }

      // Load custom tools from localStorage
      const savedTools = localStorage.getItem("customValidationTools");
      if (savedTools) {
        try {
          const tools = JSON.parse(savedTools);
          setCustomTools(tools);
        } catch (e) {
          console.error("Failed to parse saved tools", e);
        }
      }
    };

    loadData();
  }, []);

  // Combine tools with deduplication - API tools take priority, then static tools, then custom tools
  const allTools = useMemo(() => {
    // Start with static tools
    const combinedTools = [...staticTools];

    // Add API tools, replacing any static tools with same name/displayName
    const staticToolNames = new Set(
      staticTools.map((tool) => tool.displayName || tool.name)
    );

    const uniqueApiTools = apiTools.filter(
      (apiTool) => !staticToolNames.has(apiTool.displayName || apiTool.name)
    );
    combinedTools.push(...uniqueApiTools);

    // Track all existing tool names (static + API)
    const existingToolNames = new Set(
      combinedTools.map((tool) => tool.displayName || tool.name)
    );

    // Only add custom tools that don't already exist in static or API tools
    const uniqueCustomTools = customTools.filter(
      (customTool) =>
        !existingToolNames.has(customTool.displayName || customTool.name)
    );

    // Clean up localStorage - remove tools that are now available via static or API
    if (
      (apiTools.length > 0 || staticTools.length > 0) &&
      customTools.length > 0
    ) {
      const duplicateCustomTools = customTools.filter((customTool) =>
        existingToolNames.has(customTool.displayName || customTool.name)
      );

      if (duplicateCustomTools.length > 0) {
        const cleanedCustomTools = customTools.filter(
          (customTool) =>
            !existingToolNames.has(customTool.displayName || customTool.name)
        );
        localStorage.setItem(
          "customValidationTools",
          JSON.stringify(cleanedCustomTools)
        );
        setCustomTools(cleanedCustomTools);
      }
    }

    return [...combinedTools, ...uniqueCustomTools];
  }, [apiTools, customTools, staticTools]);
  const pendingTools = allTools.filter((t) => t.status === "pending");
  const approvedTools = allTools.filter((t) => t.status === "approved");

  const handleValidateTool = (tool: Tool) => {
    if (!tool.inputFields || tool.inputFields.length === 0) {
      ToastMessageUtil.show(
        "This tool has no input fields to validate, Please click Refresh button.",
        ToastVariant.ERROR
      );
      return;
    }

    // Navigate to validation route with tool data
    navigate(MainRoutes.TOOL_VALIDATION.replace(":toolId", tool.id), {
      state: { tool }
    });
  };

  const handleDeleteTool = (toolId: string) => {
    const updatedTools = customTools.filter((t) => t.id !== toolId);
    setCustomTools(updatedTools);
    localStorage.setItem("customValidationTools", JSON.stringify(updatedTools));
    setToolToDelete(null);
    ToastMessageUtil.show("Tool deleted successfully", ToastVariant.SUCCESS);
  };

  // Remove conditional rendering for validation - now handled by separate route
  // Main tools list view
  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="available-tools">
        <HubHeader
          className="header"
          title="Available Tools"
          description=" Browse and explore available validation and processing tools"
          showBackButton={true}
          backRoute="/extraction-config"
        />
        <div className="available-tools__container">
          {/* Loading State */}
          {isLoadingTools && (
            <div
              className="available-tools__loading"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "3rem",
                flexDirection: "column",
                gap: "1rem"
              }}
            >
              <CircularProgress size={32} />
              <p>Loading available tools...</p>
            </div>
          )}

          {/* Main Content */}
          {!isLoadingTools && (
            <>
              {/* Pending Approval Section */}
              {pendingTools.length > 0 && (
                <div className="available-tools__section">
                  <div className="available-tools__section-header">
                    <div className="available-tools__section-title">
                      <h2>Pending Approval</h2>
                      <div className="badge badge--pending">
                        {pendingTools.length}
                      </div>
                    </div>
                    <div className="available-tools__create-button available-tools__create-button--with-refresh">
                      <Button
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                        className="btn btn--secondary btn--large btn--refresh"
                      >
                        <RefreshIcon
                          className={`icon icon--refresh ${
                            isRefreshing ? "spinning" : ""
                          }`}
                        />
                        {isRefreshing ? "Refreshing..." : "Refresh"}
                      </Button>
                      <Button
                        onClick={() => navigate(MainRoutes.TOOL_DEVELOPMENT)}
                        className="btn btn--primary btn--large btn--create-red"
                      >
                        + Create New Tool
                      </Button>
                    </div>
                  </div>
                  <div className="tools-list">
                    {pendingTools.map((tool) => (
                      <div
                        key={tool.id}
                        className="available-tools__tool-card available-tools__tool-card--pending"
                        onClick={() => handleValidateTool(tool)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="available-tools__tool-card__content">
                          <div className="available-tools__tool-card__icon available-tools__tool-card__icon--pending">
                            <WrenchIcon className="icon" />
                          </div>
                          <div className="available-tools__tool-card__body">
                            <div className="available-tools__tool-card__title-row">
                              <div className="available-tools__tool-card__title-row-left">
                                <h3>{tool.displayName}</h3>
                                <div className="status-badge status-badge--pending">
                                  Pending
                                </div>
                              </div>
                              <div className="available-tools__tool-card__title-row-right">
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleValidateTool(tool);
                                  }}
                                  className="btn btn--primary btn--small"
                                >
                                  {/* <FlaskIcon className="icon" /> */}
                                  <PendingActionsOutlinedIcon className="icon" />
                                  Validate Tool
                                </Button>
                              </div>
                            </div>
                            <p className="available-tools__tool-card__description">
                              {tool.description}
                            </p>

                            {/* {tool.inputFields &&
                              tool.inputFields.length > 0 && (
                                <div className="available-tools__tool-card__fields">
                                  <p className="available-tools__tool-card__fields-label">
                                    Field Names:
                                  </p>
                                  <div className="available-tools__tool-card__fields-list">
                                    {tool.inputFields.map((field, idx) => (
                                      <span
                                        key={idx}
                                        className="available-tools__tool-card__fields-item available-tools__tool-card__fields-item--pending"
                                      >
                                        {field}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )} */}
                          </div>
                          {/* {tool.isCustom && (
                            <button
                              className="available-tools__tool-card__delete-btn"
                              onClick={() => setToolToDelete(tool.id)}
                            >
                              <TrashIcon className="icon" />
                            </button>
                          )} */}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Approved Tools Section */}
              <div className="available-tools__section">
                <div className="available-tools__section-header">
                  <div className="available-tools__section-header-left">
                    <h2>Approved Tools</h2>
                    <div className="badge badge--approved">
                      {approvedTools.length}
                    </div>
                  </div>
                  <div className="available-tools__create-button">
                    <Button
                      onClick={() => navigate(MainRoutes.TOOL_DEVELOPMENT)}
                      className="btn btn--primary btn--large btn--create-red"
                    >
                      + Create New Tool
                    </Button>
                  </div>
                </div>
                <div className="tools-list">
                  {approvedTools.map((tool) => (
                    <div
                      key={tool.id}
                      className="available-tools__tool-card available-tools__tool-card--approved"
                    >
                      <div className="available-tools__tool-card__content">
                        <div className="available-tools__tool-card__icon available-tools__tool-card__icon--approved">
                          <WrenchIcon className="icon" />
                        </div>
                        <div className="available-tools__tool-card__body">
                          <div className="available-tools__tool-card__title-row">
                            <h3>{tool.displayName}</h3>
                            {tool.isCustom && (
                              <div className="status-badge status-badge--approved">
                                <CheckCircleIcon className="icon" />
                                Approved
                              </div>
                            )}
                          </div>
                          <p className="available-tools__tool-card__description">
                            {tool.description}
                          </p>

                          {/* {tool.inputFields && tool.inputFields.length > 0 && (
                            <div className="available-tools__tool-card__fields">
                              <p className="available-tools__tool-card__fields-label">
                                Field Names:
                              </p>
                              <div className="available-tools__tool-card__fields-list">
                                {tool.inputFields.map((field, idx) => (
                                  <span
                                    key={idx}
                                    className="available-tools__tool-card__fields-item"
                                  >
                                    {field}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )} */}
                        </div>
                        {/* {tool.isCustom && (
                          <button
                            className="available-tools__tool-card__delete-btn"
                            onClick={() => setToolToDelete(tool.id)}
                          >
                            <TrashIcon className="icon" />
                          </button>
                        )} */}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* <div className="available-tools__create-button">
            <Button
              onClick={() => navigate(MainRoutes.TOOL_DEVELOPMENT)}
              className="btn btn--primary btn--large"
            >
              + Create New Tool
            </Button>
          </div> */}
            </>
          )}
        </div>

        {/* {toolToDelete && (
          <div className="modal-overlay" onClick={() => setToolToDelete(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Tool</h3>
              <p>
                Are you sure you want to delete this tool? This action cannot be
                undone.
              </p>
              <div className="modal-content-actions">
                <Button
                  onClick={() => setToolToDelete(null)}
                  className="btn btn--secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => toolToDelete && handleDeleteTool(toolToDelete)}
                  className="btn btn--danger"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )} */}
      </div>
    </>
  );
};

export default AvailableTools;
